import { jsPDF } from 'jspdf';
import { ref } from 'vue';
import axios from 'axios';

// IMPORTACIONES: ASUME que estas funciones están disponibles
import { formatCurrency, formatDate, getImageBase64 } from './useUtils';
import { useQrGenerator } from './useQrGenerator';
import { useNumberToText } from './useNumbersToText';
import { useLocationMapper } from './useLocationMapper';

/** Constantes de Estilo */
/*const BROWN_R = 112;
const BROWN_G = 49;
const BROWN_B = 58;*/
const MARGIN_X = 10;
const DTE_BLOCK_RADIUS = 4; // Radio de redondeo
const TEXT_LINE_HEIGHT = 4.5; // Altura real que ocupa una línea de texto
const ROW_HEIGHT = 7; // Altura mínima de una fila de tabla

/** Posiciones Fijas del Header y Footer (basadas en Letter 215.9mm x 279.4mm) */
const HEADER_HEIGHT_STATIC = 100; // Altura del encabezado
const FOOTER_HEIGHT_STATIC = 80; // Altura del pie de página

const TABLE_START_Y = MARGIN_X + HEADER_HEIGHT_STATIC; // Y de inicio de la tabla
const PAGE_MAX_Y = 280 - MARGIN_X; // Altura máxima de la página
const TABLE_MAX_DETAIL_Y = PAGE_MAX_Y - FOOTER_HEIGHT_STATIC; // Máx Y donde puede ir un detalle


function getCompanyColors(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 112, g: 49, b: 58 }; // Fallback al BROWN original
}
// --- 🛑 Funciones de Dibujo Estático (Header & Footer) 🛑 ---

/**
 * Dibuja la cabecera del documento (Logo, Emisor, Datos Fiscales DTE, y Cliente).
 */
function drawHeader(doc, headerData, config, logoBase64, qrBase64Ready, deptoDesc, muniDesc, startY = MARGIN_X) {
    const { r, g, b } = getCompanyColors(config.primary_color);
    const {
        client_name, client_nit, client_address, client_phone, client_nrc, client_document_type,
        client_activity_code, client_activity_desc, client_email, dte_url_code, dte_trace_code, dte_stamp_code,
        full_number, invoice_type, dte_type, date_issued
    } = headerData;
    console.log(dte_url_code)
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = MARGIN_X;
    let currentY = startY;

    const labelWidth = 25;

    // Helper functions
    const documentTypeMap = { '13': 'DUI (13)', '36': 'NIT (36)' };
    const getDocumentTypeText = (code) => documentTypeMap[code] || code;

    // --- 0. LOGO Y EMISOR ---
    const logoMaxHeight = 15; // Altura máxima deseada
    const logoMaxWidth = 50;  // Ancho máximo permitido para que no choque con el bloque DTE

    if (logoBase64) {
        // Obtener dimensiones reales de la imagen
        const imgProps = doc.getImageProperties(logoBase64);

        // Calcular ancho proporcional basado en la altura máxima
        let finalLogoWidth = (imgProps.width * logoMaxHeight) / imgProps.height;
        let finalLogoHeight = logoMaxHeight;

        // Validar que no exceda el ancho máximo (opcional)
        if (finalLogoWidth > logoMaxWidth) {
            finalLogoWidth = logoMaxWidth;
            finalLogoHeight = (imgProps.height * logoMaxWidth) / imgProps.width;
        }

        doc.addImage(logoBase64, 'PNG', marginX, currentY, finalLogoWidth, finalLogoHeight);
    }

    // Ajustamos yAfterLogo usando la altura real calculada para que el texto no se encime
    let yAfterLogo = currentY + logoMaxHeight + 5;

    const issuerX = marginX;
    const maxIssuerWidth = (pageWidth / 2.2) - marginX; // Ajuste de ancho para no chocar con el bloque DTE

    // --- NOMBRE DEL EMISOR (DINÁMICO) ---
    doc.setFontSize(10).setFont(undefined, 'bold');
    const emitterNameLines = doc.splitTextToSize(config.emitter_name, maxIssuerWidth);
    doc.text(emitterNameLines, issuerX, yAfterLogo);

    // Calculamos cuánto espacio ocupó el nombre (cada línea mide aprox 4mm-5mm)
    const nameAdvance = emitterNameLines.length * 4.5;
    let currentIssuerY = yAfterLogo + nameAdvance;

    // --- DATOS BÁSICOS ---
    doc.setFontSize(8).setFont(undefined, 'normal');
    doc.text(`NIT: ${config.emitter_nit}`, issuerX, currentIssuerY);
    doc.text(`NRC: ${config.nrc}`, issuerX, currentIssuerY + 4);
    currentIssuerY += 8;

    // --- ACTIVIDAD ECONÓMICA (DINÁMICA) ---
    const econEmiter = doc.splitTextToSize(`ACTIVIDAD ECONÓMICA: ${config.dte_activity_code} - ${config.dte_activity_desc}`, maxIssuerWidth);
    doc.text(econEmiter, issuerX, currentIssuerY);

    // Calculamos el avance de la actividad
    const econAdvance = econEmiter.length * 4;
    let nextY = currentIssuerY + econAdvance + 2;

    // --- DIRECCIÓN Y CONTACTO ---
    doc.text(`DIRECCIÓN: ${config.address}, ${config.city}, ${config.country}`, issuerX, nextY);
    doc.text(`TEL: ${config.phone_number || 'N/A'}`, issuerX, nextY + 4);
    doc.text(`EMAIL: ${config.email || 'N/A'}`, issuerX, nextY + 8);

    // Punto final para que el bloque de cliente sepa dónde empezar
    //let yAfterEmisor = nextY + 12;
    // Ajustamos el punto final del emisor para que los siguientes bloques sepan dónde empezar
    let yAfterEmisor = nextY + 10;
    //let yAfterEmisor = yAfterLogo + 25;

    // --- 1. BLOQUE FISCAL DTE (QR + CÓDIGOS) ---
    const dteBlockWidth = 120;
    const dteBlockX = pageWidth - marginX - dteBlockWidth;
    const dteBlockY = yAfterLogo-10;//currentY;
    const qrSize = 25;
    const lineHeight = 5;
    const dataX = dteBlockX + qrSize + 5;
    const dteBlockHeight = 45;

    doc.setFontSize(10).setFont(undefined, 'bold');
    const docTypeText = () => {
        if (dte_type === '01') return 'FACTURA DE CONSUMIDOR FINAL';
        if (dte_type === '03') return 'COMPROBANTE DE CRÉDITO FISCAL';
        if (dte_type === '05') return 'NOTA DE CRÉDITO';
        if (dte_type === '06') return 'NOTA DE DÉDITO';
        if (dte_type === '14') return 'FACTURA DE SUJETO EXCLUIDO';
        return invoice_type || 'COMPROBANTE';
    };

    if (config.send_dte == 1) {
        doc.text('DOCUMENTO TRIBUTARIO ELECTRÓNICO', dteBlockX + dteBlockWidth / 2, dteBlockY + 4, { align: 'center' });
        doc.setFontSize(10).text(docTypeText(), dteBlockX + dteBlockWidth / 2, dteBlockY + 8, { align: 'center' });

        doc.setLineWidth(0.5).setDrawColor(r, g, b);
        doc.roundedRect(dteBlockX, dteBlockY, dteBlockWidth, dteBlockHeight, DTE_BLOCK_RADIUS, DTE_BLOCK_RADIUS, 'S');
        doc.setDrawColor(0);

        if (qrBase64Ready) {
            doc.addImage(qrBase64Ready, 'PNG', dteBlockX + 1, dteBlockY + 10, qrSize, qrSize);
        } else {
            doc.setFontSize(8).text("QR NO DISPONIBLE", dteBlockX + 5, dteBlockY + 25);
        }

        let dataY = dteBlockY + 14;
        doc.setFontSize(6);

        doc.setFont(undefined, 'bold').text('Código de generación:', dataX, dataY);
        doc.setFont(undefined, 'normal').text(dte_trace_code || 'N/A', dataX + labelWidth, dataY);
        dataY += lineHeight;

        doc.setFont(undefined, 'bold').text('No. de control del DTE:', dataX, dataY);
        doc.setFont(undefined, 'normal').text(full_number || 'N/A', dataX + labelWidth, dataY);
        dataY += lineHeight;

        doc.setFont(undefined, 'bold').text('Sello de recepción:', dataX, dataY);
        doc.setFont(undefined, 'normal').text(dte_stamp_code || 'N/A', dataX + labelWidth, dataY);
        dataY += lineHeight;

        doc.setFont(undefined, 'bold');
        doc.text(`Fecha y hora generación: ${formatDate(date_issued)}`, dataX, dataY);
    } else {
        doc.text('DOCUMENTO TRIBUTARIO', dteBlockX + dteBlockWidth / 2, dteBlockY + 4, { align: 'center' });
        doc.setFontSize(10).text('FACTURA', dteBlockX + dteBlockWidth / 2, dteBlockY + 8, { align: 'center' });
        // Simplemente ponemos el número de factura para documentos no electrónicos
        doc.setFontSize(6).setFont(undefined, 'normal');
        doc.text(`NUMERO: ${full_number || 'N/A'}`, dataX, dteBlockY + 14);
        doc.text(`Fecha y hora emisión: ${formatDate(date_issued)}`, dataX, dteBlockY + 19);
    }

    currentY = Math.max(yAfterEmisor, dteBlockY + dteBlockHeight) + 5;
    // 🛑 Reducción de la altura del bloque de Cliente aquí para que el total no exceda 100mm 🛑
    const clientBlockHeight = 35; // REDUCIDO de 45 a 35
    const col1X = marginX + 2;
    const col2X = pageWidth / 2 + 5;
    let clientY = currentY;

    doc.setLineWidth(0.5).setDrawColor(r, g, b);
    doc.roundedRect(marginX, clientY, pageWidth - (2 * marginX), clientBlockHeight, DTE_BLOCK_RADIUS, DTE_BLOCK_RADIUS, 'S');
    doc.setDrawColor(0);
    let currentClientY = clientY + 3;

    doc.setFontSize(8);

    // Línea 1: Cliente / Documento
    const maxClientNameWidth = 70;
    const clientNameLines = doc.splitTextToSize(client_name || 'CONSUMIDOR FINAL', maxClientNameWidth);

    doc.setFont(undefined, 'bold').text('CLIENTE:', col1X, currentClientY);
    doc.setFont(undefined, 'normal').text(clientNameLines, col1X + labelWidth, currentClientY);

    doc.setFont(undefined, 'bold').text('DOCUMENTO:', col2X, currentClientY);
    doc.setFont(undefined, 'normal').text(client_nit || 'N/A', col2X + labelWidth, currentClientY);

    // Calculamos el avance basado en la altura del nombre del cliente
    let verticalAdvance = Math.max(TEXT_LINE_HEIGHT, clientNameLines.length * TEXT_LINE_HEIGHT);
    currentClientY += verticalAdvance;

    // Línea 2: Tipo Doc / NRC
    doc.setFont(undefined, 'bold').text('TIPO DOC:', col1X, currentClientY);
    doc.setFont(undefined, 'normal').text(getDocumentTypeText(client_document_type || '36'), col1X + labelWidth, currentClientY);

    doc.setFont(undefined, 'bold').text('NRC:', col2X, currentClientY);
    doc.setFont(undefined, 'normal').text(client_nrc || 'N/A', col2X + labelWidth, currentClientY);
    currentClientY += TEXT_LINE_HEIGHT;

    // Línea 3: Actividad / Correo
    doc.setFont(undefined, 'bold').text('ACTIVIDAD:', col1X, currentClientY);
    doc.setFont(undefined, 'normal').text(`${client_activity_code} / ${client_activity_desc }`|| '99999 / Venta al consumidor', col1X + labelWidth, currentClientY);

    doc.setFont(undefined, 'bold').text('CORREO:', col2X, currentClientY);
    doc.setFont(undefined, 'normal').text(client_email || 'N/A', col2X + labelWidth, currentClientY);
    currentClientY += TEXT_LINE_HEIGHT;

    // Línea 4: Depto/Mun / Teléfono
    doc.setFont(undefined, 'bold').text('DEPTO/MUN:', col1X, currentClientY);
    doc.setFont(undefined, 'normal').text(`${deptoDesc.toUpperCase() || 'N/A'} / ${muniDesc.toUpperCase() || 'N/A'}`, col1X + labelWidth, currentClientY);

    doc.setFont(undefined, 'bold').text('TELÉFONO:', col2X, currentClientY);
    doc.setFont(undefined, 'normal').text(client_phone || 'N/A', col2X + labelWidth, currentClientY); // Ajustado a labelWidth para alineación
    currentClientY += TEXT_LINE_HEIGHT;

    // Línea 5: Dirección (Al final)
    doc.setFont(undefined, 'bold').text('DIRECCIÓN:', col1X, currentClientY);
    const addressLines = doc.splitTextToSize(client_address || 'Sin dirección', pageWidth - col1X - labelWidth - marginX);
    doc.setFont(undefined, 'normal').text(addressLines, col1X + labelWidth, currentClientY);

    // Devolvemos TABLE_START_Y (110)
    return TABLE_START_Y;
}

/**
 * Dibuja el pie de página (Totales, Notas y Firmas).
 */
function drawFooter(doc, headerData, config, totalEnLetras, isReimpresion, footerY) {
    const { r, g, b } = getCompanyColors(config.primary_color);
    const {
        client_name, client_nit, payment_condition, payment_type, term_type, term, due_date
    } = headerData;
    footerY=footerY-10
    console.log(totalEnLetras)
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = MARGIN_X;
    let notesY = footerY; // Mover el puntero Y para las notas
    /*let currentY = footerY;*/
    //const currencySymbol = config?.currency_symbol || '$';
    const footerNotes = config?.footer_notes || "Gracias por su compra.";
    const textWidth = pageWidth - (2 * marginX) - 50;
    //const totalsX = pageWidth - marginX - 60;
    const paymentOptions = {
        '01': '01 - Billetes y monedas', '02': '02 - Tarjeta Débito', '03': '03 - Tarjeta Crédito',
        '04': '04 - Cheque', '05': '05 - Transferencia-Depósito Bancario', '08': '08 - Dinero electrónico',
        '09': '09 - Monedero electrónico', '11': '11 - Bitcoin', '12': '12 - Otras Criptomonedas',
        '13': '13 - Cuentas por pagar del receptor', '14': '14 - Giro bancario', '99': '99 - Otros'
    };
    const term_types_map = { '01': 'Días', '02': 'Meses', '03': 'Años' };
    const termOption = term_types_map[term_type] || term_type;
    const paymentOption = paymentOptions[payment_type] || payment_type;
    const lineHeight = 4;
    doc.setLineWidth(0.5).setDrawColor(r, g, b);
    doc.line(marginX, footerY -3, pageWidth - MARGIN_X, footerY - 3);
        // --- 3. BLOQUE DE ENTREGA Y RECEPCIÓN (Firmas) ---
    const blockHeight = 25;
    const signatureBlockY = PAGE_MAX_Y - blockHeight - 5; // Posición estática en el pie
    const blockWidth = pageWidth - (2 * marginX);

    // --- 2. NOTAS DE PAGO ---
    doc.setFontSize(8);

    // Cantidad en Letras
    doc.setFont(undefined, 'bold').text('CANTIDAD EN LETRAS:', marginX, notesY);
    doc.setFont(undefined, 'normal');
    const totalEnLetrasLines = doc.splitTextToSize(totalEnLetras, textWidth);
    doc.text(totalEnLetrasLines, marginX, notesY + 4);
    notesY += (totalEnLetrasLines.length * 4) + 4;

    // Condición de Pago
    const conditionText = payment_condition === 1 ? 'CONTADO' : 'CRÉDITO';
    doc.setFont(undefined, 'bold').text('CONDICIÓN:', marginX, notesY);
    doc.setFont(undefined, 'normal').text(conditionText, marginX + 35, notesY);
    notesY += 5;

    if (payment_condition == 1) {
        // Forma de Pago (Contado)
        doc.setFont(undefined, 'bold').text('FORMA DE PAGO:', marginX, notesY);
        doc.setFont(undefined, 'normal').text(paymentOption, marginX + 35, notesY);
        notesY += 5;
    } else {
        // Plazo y Vencimiento (Crédito)
        const plazoText = `${term || 0} ${termOption.toString().toUpperCase()}`;
        doc.setFont(undefined, 'bold').text('PLAZO:', marginX, notesY);
        doc.setFont(undefined, 'normal').text(plazoText, marginX + 35, notesY);
        notesY += 5;
        doc.setFont(undefined, 'bold').text('FECHA VENCIMIENTO:', marginX, notesY);
        doc.setFont(undefined, 'normal').text(formatDate(due_date).split(' ')[0], marginX + 35, notesY);
        notesY += 5;
    }

    // Notas al Pie
    doc.setFont(undefined, 'bold').text('NOTAS AL PIE:', marginX, notesY);
    doc.setFont(undefined, 'normal');
    const notesLines = doc.splitTextToSize(footerNotes, textWidth);
    doc.text(notesLines, marginX, notesY + 4);
    notesY += (notesLines.length + 4);

    doc.setLineWidth(0.5).setDrawColor(r, g, b);
    doc.roundedRect(marginX, signatureBlockY, blockWidth, blockHeight, 3, 3, 'S');
    doc.setDrawColor(0);

    const col1StartX = marginX + 3;
    const col2StartX = pageWidth / 2 + 5;
    const textDataWidth = (blockWidth / 2) - 10;
    const yLabelName = signatureBlockY + 4;
    const yLabelDoc = signatureBlockY + 16;
    const nombreEntrega = config.emitter_name || '________________________________';
    const documentoEntrega = config.emitter_nit || '________________________________';
    const nombreRecibe = client_name || '________________________________';
    const documentoRecibe = client_nit || '________________________________';

    // Columna 1: Entrega
    doc.setFontSize(8).setFont(undefined, 'bold').text('Nombre entrega:', col1StartX, yLabelName);
    doc.setFont(undefined, 'normal').text(doc.splitTextToSize(nombreEntrega, textDataWidth), col1StartX, yLabelName + lineHeight);
    doc.setFont(undefined, 'bold').text('Documento entrega:', col1StartX, yLabelDoc);
    doc.setFont(undefined, 'normal').text(documentoEntrega, col1StartX, yLabelDoc + lineHeight);

    // Columna 2: Recibe
    doc.setFontSize(8).setFont(undefined, 'bold').text('Nombre recibe:', col2StartX, yLabelName);
    doc.setFont(undefined, 'normal').text(doc.splitTextToSize(nombreRecibe, textDataWidth), col2StartX, yLabelName + lineHeight);
    doc.setFont(undefined, 'bold').text('Documento recibe:', col2StartX, yLabelDoc);
    doc.setFont(undefined, 'normal').text(documentoRecibe, col2StartX, yLabelDoc + lineHeight);

    // Marca de Reimpresión (Siempre en la última página)
    if (isReimpresion) {
        const reimpresionY = PAGE_MAX_Y;
        doc.setFontSize(12).setFont(undefined, 'bold').setTextColor(200, 0, 0);
        doc.text('**REIMPRESION**', pageWidth / 2, reimpresionY, { align: 'center' });
        doc.setTextColor(0);
    }
}

function drawFooterTotal(doc, headerData, config, totalEnLetras, footerY){
    const { r, g, b } = getCompanyColors(config.primary_color);
    const {
         total, subtotal, total_taxes, total_discount, sub_total_brut,
        retention_amount, dte_type
    } = headerData;
    footerY=footerY-10
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = MARGIN_X;
    let currentY = footerY;
    const currencySymbol = config?.currency_symbol || '$';
    //const footerNotes = config?.footer_notes || "Gracias por su compra.";
    //const textWidth = pageWidth - (2 * marginX) - 50;
    const totalsX = pageWidth - marginX - 60;
    //const lineHeight = 4;
    //doc.setLineWidth(0.5).setDrawColor(r, g, b);
    //doc.line(marginX, footerY -10, pageWidth - MARGIN_X, footerY - 10);
    // Helper options

    // --- 1. TOTALES Y RESUMEN ---
    doc.setFontSize(6).setFont(undefined, 'normal');

    doc.text('SUMA TOTAL DE OPERACIONES:', totalsX, currentY);
    doc.text(formatCurrency(Number(sub_total_brut).toFixed(2) || 0, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 5;

    doc.text('(-) DESC., BONIFIC. REBAJAS NO SUJETAS:', totalsX, currentY);
    doc.setTextColor(200, 0, 0);
    doc.text(formatCurrency(Number(total_discount).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 5;
    doc.setTextColor(0);
    doc.text('(-) DESC., BONIFIC. REBAJAS EXENTAS:', totalsX, currentY);
    doc.setTextColor(200, 0, 0);
    doc.text(formatCurrency(Number(total_discount).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 5;
    doc.setTextColor(0);
    doc.text('(-) DESC., BONIFIC. REBAJAS:', totalsX, currentY);
    doc.setTextColor(200, 0, 0);
    doc.text(formatCurrency(Number(total_discount).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 5;
    doc.setTextColor(0);
    // IVA (13%)
    doc.text('IMPUESTO AL VALOR AGREGADO 13%:', totalsX, currentY);
    const ivaValue = dte_type == '01' ? 0.00 : total_taxes;
    doc.text(formatCurrency(Number(ivaValue).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 4;

    doc.text('SUBTOTAL:', totalsX, currentY);
    doc.text(formatCurrency(Number(subtotal).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 5;

    // IVA PERCEPCIÓN
    doc.text('(+) IVA PERCEPCIÓN 1%:', totalsX, currentY);
    doc.text(formatCurrency(0.00, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 4;

    // RETENCIONES (diferente para Sujeto Excluido DTE 14 vs otros)
    if (dte_type === '14') {
        doc.text('(-) IVA RETENCIÓN 1%:', totalsX, currentY);
        doc.setTextColor(200, 0, 0);
        doc.text(formatCurrency(0.00, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
        currentY += 4;
        doc.setTextColor(0);
        doc.text('RETENCIÓN RENTA:', totalsX, currentY);
        doc.setTextColor(200, 0, 0);
        doc.text(formatCurrency(Number(retention_amount).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
        currentY += 4;
    } else {
        doc.text('(-) IVA RETENCIÓN 1%:', totalsX, currentY);
        doc.setTextColor(200, 0, 0);
        doc.text(formatCurrency(Number(retention_amount).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
        currentY += 4;
        doc.setTextColor(0);
        doc.text('RETENCIÓN RENTA:', totalsX, currentY);
        doc.setTextColor(200, 0, 0);
        doc.text(formatCurrency(0.00, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
        currentY += 4;
    }
    doc.setTextColor(0);
    doc.text('TOTAL OTROS MONTOS NO AFECTOS:', totalsX, currentY);
    doc.text(formatCurrency(0.00, undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 4;

    doc.setDrawColor(r, g, b).setLineWidth(0.5);
    doc.line(totalsX - 5, currentY, pageWidth - marginX, currentY);
    doc.setDrawColor(0);
    currentY += 5;

    doc.setFontSize(8).setFont(undefined, 'bold');
    doc.text('TOTAL A PAGAR:', totalsX - 5, currentY);
    doc.text(formatCurrency(Number(total).toFixed(2), undefined, currencySymbol), pageWidth - marginX - 2, currentY, { align: 'right' });
    currentY += 10;



}

/**
 * Añade la numeración de página "Página X de Y" en la parte inferior de cada página.
 */
function drawPageNumber(doc, totalPages) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = MARGIN_X;
    // Se dibujará en la parte inferior, sobre el espacio del footer.
    const pageNumY = PAGE_MAX_Y - 2;

    doc.setFontSize(8).setFont(undefined, 'bold').setTextColor(0);

    // Iteramos sobre todas las páginas creadas para añadir la numeración
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const text = `Página ${i} de ${totalPages}`;
        doc.text(text, pageWidth - marginX, pageNumY, { align: 'right' });
    }
    doc.setTextColor(0); // Restaura el color
}

// Función emailDTE (Mantenida sin cambios)
const emailDTE = async (pdfBase64, email, facturaNumber, dte_trace_code, company) => {
    if (!email) {
        console.warn("Email del cliente no disponible para el envío.");
        return;
    }

    try {
        const apiURL = 'https://dicovaspos.therealdevs.pro/index.php/';
        const token = localStorage.getItem('accessToken');
        const response = await axios.post(`${apiURL}email/send_dte`, {
            email: email,
            subject: `Copia de DTE ${facturaNumber} [Enviado por DICOVAS]`,
            filename: `DTE_${facturaNumber}.pdf`,
            pdf_base64: pdfBase64,
            trace_code: dte_trace_code,
            company:company
        }, { headers: { 'Authorization': `Bearer ${token}` } }, { keepalive: true });

        console.log(`DTE enviado a ${email}. Mensaje:`, response.data.message);
    } catch (error) {
        console.error("Fallo al enviar el DTE por correo:", error.response?.data?.error || error.message);
    }
};

// --- 🛑 Función Principal Corregida 🛑 ---

/**
 * Genera el PDF en formato Carta para un Comprobante de Crédito Fiscal o Factura, según normas de El Salvador.
 * @param {Object} factura - Objeto completo de la factura (header y lines).
 * @param {Object} companyConfig - Objeto de configuración de la empresa.
 * @param {boolean} isReimpresion - Si es true, añade la marca de REIMPRESION.
 * @returns {Promise<void>}
 */
export function useDTEFiscal(factura, companyConfig, isReimpresion = false, isMassExport = false) {
    return new Promise((resolve) => {
        const { header, lines } = factura;
        const config = companyConfig;

        if (lines.length === 0) {
            alert('La factura no tiene líneas de producto para imprimir.');
            return resolve();
        }
        const mostrarReimpresion = isMassExport ? false : isReimpresion;
        const { numberToText } = useNumberToText();
        const { getLocationDescription, fetchLocations } = useLocationMapper();
        const dteUrlRef = ref(header.dte_url_code);
        const { qrDataUrl, generateQr } = useQrGenerator(dteUrlRef);
        const totalEnLetras = numberToText(header.total);

        const doPrint = async () => {
            await fetchLocations();
            const deptoDesc = getLocationDescription(header.client_department, true);
            const muniDesc = getLocationDescription(header.client_municipality, true);

            const logoBase64 = await getImageBase64(config?.logo_url || '');
            if (header.dte_url_code) {
                try {
                    await generateQr(header.dte_url_code);
                } catch (e) {
                    console.error("Fallo al generar QR para PDF:", e);
                }
            }
            const qrBase64Ready = qrDataUrl.value;

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'letter'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const currencySymbol = config?.currency_symbol || '$';

            // Definiciones de columna para la tabla
            const totalW = 20;
            const puW = 20;
            const cantW = 15;
            const descW = pageWidth - (2 * MARGIN_X) - totalW - puW - cantW;
            const col = {
                desc: MARGIN_X,
                cant: MARGIN_X + descW,
                pu: MARGIN_X + descW + cantW,
                total: pageWidth - MARGIN_X - totalW
            };
            const descWidthForWrapping = col.cant - col.desc - 5;

            let linesIndex = 0;
            let currentPage = 1;
            console.log(currentPage)
            // Posición fija del footer
            const footerY = PAGE_MAX_Y - FOOTER_HEIGHT_STATIC + 5;

            /**
             * Dibuja la cabecera y el encabezado de la tabla.
             * @returns {number} La posición Y de inicio para el primer detalle de la tabla.
             */
            const drawPageHeader = () => {
                const { r, g, b } = getCompanyColors(config.primary_color);
                const startY = MARGIN_X;
                drawHeader(doc, header, config, logoBase64, qrBase64Ready, deptoDesc, muniDesc, startY);

                // Dibujar encabezados de tabla
                const tableY = TABLE_START_Y - 5;
                doc.setFontSize(7).setFont(undefined, 'bold');
                doc.text('DESCRIPCIÓN', col.desc + 2, tableY + 5);
                doc.text('CANT.', col.cant + cantW / 2, tableY + 5, { align: 'center' });
                doc.text('PRECIO UNIT.', col.pu + puW / 2, tableY + 5, { align: 'center' });
                doc.text('TOTAL', col.total + totalW - 2, tableY + 5, { align: 'right' });
                doc.setLineWidth(0.5).setDrawColor(r, g, b);
                doc.line(col.desc, tableY + ROW_HEIGHT, pageWidth - MARGIN_X, tableY + ROW_HEIGHT);
                doc.setDrawColor(0);
                doc.setFontSize(7).setFont(undefined, 'normal');

                // 🛑 LLAMADA AL FOOTER TEMPORAL/REPETITIVO 🛑
                // Esto es parte de tu diseño original y se repite en cada página.
                drawFooter(doc, header, config, totalEnLetras, mostrarReimpresion, footerY);

                return tableY + ROW_HEIGHT + 4;
            };

            // 1. Inicializar detailY en la primera página
            let detailY = drawPageHeader();
            doc.setPage(1); // Nos aseguramos de estar en la primera página para dibujar los detalles

            // --- 2. PAGINACIÓN Y DIBUJO DE LÍNEAS DE DETALLE CORREGIDO (Manejo de Párrafos Largos) ---
            while (linesIndex < lines.length) {
                doc.setFontSize(7).setFont(undefined, 'normal');
                const line = lines[linesIndex];
                const description = line.description || line.nombre || 'Producto';

                const fullTextToDraw = line.remainingText || description;
                const descTextLines = doc.splitTextToSize(fullTextToDraw, descWidthForWrapping);
                const fullLinesCount = descTextLines.length;

                const spaceRemaining = TABLE_MAX_DETAIL_Y - detailY;
                const maxTextLinesOnPage = Math.floor(spaceRemaining / TEXT_LINE_HEIGHT);

                let linesToDrawCount = fullLinesCount;
                let heightNeeded;
                let isFullLineDrawn = true;

                // 🛑 Determinar si fragmentamos o paginamos 🛑
                if (fullLinesCount * TEXT_LINE_HEIGHT > spaceRemaining) {

                    if (maxTextLinesOnPage >= 1) {
                        linesToDrawCount = maxTextLinesOnPage;
                        isFullLineDrawn = false;
                    } else {
                        // B. No cabe ni una sola línea: Paginamos.
                        doc.addPage();
                        currentPage++;
                        detailY = drawPageHeader();
                        continue;
                    }
                }

                heightNeeded = Math.max(ROW_HEIGHT, linesToDrawCount * TEXT_LINE_HEIGHT);

                // 4. Dibujar el texto y la data
                const textFragmentToDraw = descTextLines.slice(0, linesToDrawCount);
                doc.text(textFragmentToDraw, col.desc + 2, detailY);

                // Las columnas de cantidad/precios/total solo se imprimen la PRIMERA vez
                if (!line.remainingText) {
                    const lineTotal = line.sub_total_brut || (line.quantity * line.unit_price);
                    doc.text(parseFloat(line.quantity).toFixed(2), col.cant + cantW / 2, detailY, { align: 'center' });
                    doc.text(formatCurrency(line.unit_price, undefined, currencySymbol), col.pu + puW / 2, detailY, { align: 'center' });
                    doc.text(formatCurrency(lineTotal, undefined, currencySymbol), col.total + totalW - 2, detailY, { align: 'right' });
                }

                // 5. Gestión del avance y el índice
                detailY += heightNeeded;

                if (isFullLineDrawn) {
                    delete line.remainingText;
                    linesIndex++;
                } else {
                    // Caso fragmentado: Aún quedan líneas y debemos ir a la siguiente página.
                    const remainingLines = descTextLines.slice(linesToDrawCount);
                    line.remainingText = remainingLines.join(' ');

                    doc.addPage();
                    currentPage++;
                    detailY = drawPageHeader();
                }
            }
            // 🛑 FIN DEL BUCLE WHILE 🛑

            // --- 3. NUMERACIÓN FINAL (X de Y) ---

            // 🛑 Lógica de Paginación X de Y 🛑
            // El doc.internal.pages contiene una entrada nula al inicio, por eso restamos 1.
            const totalPages = doc.internal.pages.length - 1;
            drawPageNumber(doc, totalPages);
            drawFooterTotal(doc, header, config, totalEnLetras, footerY);


            // --- 4. FINALIZACIÓN Y APERTURA (Mismo código original) ---

            const fullNumber = header.full_number || factura.full_number;
            const fileName = `${fullNumber}.pdf`;

            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(blobUrl);

            const clientEmail = header.client_email || factura.receiver?.email;
            const facturaNumber = header.full_number;
            if (!isMassExport) {
                if (clientEmail && facturaNumber && qrBase64Ready && !isReimpresion) {
                    const base64Promise = new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = function() {
                            resolve(reader.result.split('base64,')[1]);
                        }
                    });
                    const pdfBase64 = await base64Promise;
                    await emailDTE(pdfBase64, clientEmail, facturaNumber, header.dte_trace_code,config.id);
                }
            }
            resolve();
        }

        doPrint();
    });
}